type RectLike = Pick<DOMRect, "left" | "top" | "width" | "height">;

export interface ComputeContainTransformReturn {
  scale: number;
  translateX: number;
  translateY: number;
  transform: string;
}

export interface ComputeContainOptions {
  gap?: number;
  mapOriginalBox?: RectLike | null;
  allowUpscale?: boolean;
  maxScale?: number;
}

export function computeContainTransform(
  containerEl: HTMLDivElement,
  mapEl: Element,
  opts: ComputeContainOptions = {}
): ComputeContainTransformReturn | null {
  if (!mapEl) return null;
  if (!containerEl) return null;

  const containerRect = containerEl.getBoundingClientRect();

  const mapRect = opts.mapOriginalBox || mapEl.getBoundingClientRect();

  const {
    width: containerWidth,

    height: containerHeight,
    left: containerLeft,
    top: containerTop
  } = containerRect;

  const {
    width: mapWidth,
    height: mapHeight,
    left: mapLeft,
    top: mapTop
  } = mapRect;

  if (
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    mapWidth <= 0 ||
    mapHeight <= 0
  ) {
    return null;
  }

  const allowUpscale = opts.allowUpscale ?? true;
  const gap = Math.max(0, Math.min(1, opts.gap ?? 1));

  let scale = Math.min(
    (containerWidth * gap) / mapWidth,
    (containerHeight * gap) / mapHeight
  );

  if (!allowUpscale) scale = Math.min(scale, 1);
  if (opts.maxScale != null) scale = Math.min(scale, opts.maxScale);

  const scaledMapWidth = mapWidth * scale;
  const scaledMapHeight = mapHeight * scale;

  const desiredOffsetX = (containerWidth - scaledMapWidth) / 2;
  const desiredOffsetY = (containerHeight - scaledMapHeight) / 2;

  const currentOffsetX = mapLeft - containerLeft;
  const currentOffsetY = mapTop - containerTop;

  const translateX = desiredOffsetX - currentOffsetX;
  const translateY = desiredOffsetY - currentOffsetY;

  return {
    scale,
    translateX,
    translateY,
    // !Important translate must be placed before scale
    transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`
  };
}
